import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxSelect, useComboboxOptions } from '@/components/ui/combobox-select';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Clock,
  Play,
  Pause,
  Plus,
  Trash,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/lib/store';
import { toast } from '@/hooks/use-toast';
import { showErrorToast } from '@/lib/api';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import { toISODate } from '@/lib/dates';
import { getRecentMatterIds, pushRecentMatter } from '@/lib/recent-matters';
import { TimeEntryRow, DashboardSettings } from './types';
import { 
  decimalToHHMM, 
  parseTimeInput, 
  validateHoursInput,
  formatTimeForDisplay 
} from './utils';

interface DailyTimeEntryProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  timeEntries: TimeEntryRow[];
  setTimeEntries: (entries: TimeEntryRow[]) => void;
  activeTimer: number | null;
  setActiveTimer: (id: number | null) => void;
  onRefreshStats: () => void;
  onRefreshTimeEntries: () => void;
  settings: DashboardSettings;
}

export function DailyTimeEntry({
  selectedDate,
  onDateChange,
  timeEntries,
  setTimeEntries,
  activeTimer,
  setActiveTimer,
  onRefreshStats,
  onRefreshTimeEntries,
  settings
}: DailyTimeEntryProps) {
  const { user } = useAuth();
  const { matters, clients } = useStore();
  
  const [elapsedTime, setElapsedTime] = useState<{[key: number]: number}>({});
  const [focusedHoursIndex, setFocusedHoursIndex] = useState<number | null>(null);
  const [editingHoursValue, setEditingHoursValue] = useState<string | null>(null);
  const [focusedDescriptionIndex, setFocusedDescriptionIndex] = useState<number | null>(null);
  const [focusedMatterIndex, setFocusedMatterIndex] = useState<number | null>(null);
  const [localTimeEntries, setLocalTimeEntries] = useState<TimeEntryRow[]>(timeEntries);
  const previousTimeEntriesRef = useRef<TimeEntryRow[]>([]);
  const hasInitiallyMountedRef = useRef(false);
  
  // Mutex ref to prevent concurrent timer/save operations (fixes race conditions)
  const operationInProgressRef = useRef(false);
  
  // Refs for quick entry feature
  const lastRowTriggerRef = useRef<HTMLButtonElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  // Sync parent's timeEntries to local state ONLY when user is not editing
  // This prevents cursor jumps and glitchy behavior
  useEffect(() => {
    const isUserEditing = focusedDescriptionIndex !== null || 
                         focusedHoursIndex !== null || 
                         focusedMatterIndex !== null;
    
    if (!isUserEditing) {
      // Apply alphabetical sorting ONLY on very first mount (true page load)
      // NEVER re-sort after that - entries stay in the order they appear
      if (!hasInitiallyMountedRef.current && timeEntries.length > 0 && timeEntries.some(e => !e.isNew)) {
        const sortedEntries = [...timeEntries].sort((a, b) => {
          // Keep new entries at the end
          if (a.isNew && !b.isNew) return 1;
          if (!a.isNew && b.isNew) return -1;
          
          // Non-billable entries come first, then billable
          if (!a.billable && b.billable) return -1;
          if (a.billable && !b.billable) return 1;
          
          // Within the same billable group, sort by group name then matter name (both case-insensitive)
          const matterA = matters.find(m => m.id === a.matter);
          const matterB = matters.find(m => m.id === b.matter);
          
          const groupA = (matterA?.group_name || '').toLowerCase();
          const groupB = (matterB?.group_name || '').toLowerCase();
          
          if (groupA !== groupB) {
            return groupA.localeCompare(groupB);
          }
          
          const nameA = (matterA?.name || '').toLowerCase();
          const nameB = (matterB?.name || '').toLowerCase();
          
          return nameA.localeCompare(nameB);
        });
        
        setLocalTimeEntries(sortedEntries);
        setTimeEntries(sortedEntries);
        hasInitiallyMountedRef.current = true;
      } else {
        // After initial mount, accept updates from parent but NEVER sort
        // Just accept the order from the API
        setLocalTimeEntries(timeEntries);
      }
      
      previousTimeEntriesRef.current = timeEntries;
    }
    // If user IS editing, we completely ignore the parent's updates
  }, [timeEntries, focusedDescriptionIndex, focusedHoursIndex, focusedMatterIndex, matters, setTimeEntries]);

  // Create combobox options for matters (only active matters)
  const baseMatterOptions = useComboboxOptions(
    matters.filter(m => m.status === 'active'),
    (m) => m.id.toString(),
    (m) => m.group_name ? `${m.group_name} - ${m.name}` : m.name,
    (m) => {
      // Include client name in searchable text
      const activeConfig = m.fee_sharing_configs?.find(c => c.is_active) || m.fee_sharing_configs?.[0];
      const clientNames = activeConfig?.participants?.map(p => {
        const client = clients.find(c => c.id === p.client);
        return client?.name || '';
      }).join(' ') || '';
      return `${m.name} ${m.group_name || ''} ${clientNames}`;
    }
  );

  // Recently-used matters first (in recency order), rest alphabetical as before
  const recentMatterIds = getRecentMatterIds();
  const recentMatterOptions = recentMatterIds
    .map((id) => baseMatterOptions.find((o) => o.value === id))
    .filter((o): o is NonNullable<typeof o> => Boolean(o));
  const matterOptions = [
    ...recentMatterOptions,
    ...baseMatterOptions
      .filter((o) => !recentMatterIds.includes(o.value))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  // Timer interval effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    
    if (activeTimer !== null) {
      const activeEntry = localTimeEntries.find(entry => entry.timer_active);
      
      if (activeEntry && activeEntry.id) {
        setElapsedTime(prev => ({
          ...prev,
          [activeEntry.id!]: parseFloat(activeEntry.hours) || 0
        }));
        
        intervalId = setInterval(() => {
          setElapsedTime(prev => ({
            ...prev,
            [activeEntry.id!]: (prev[activeEntry.id!] || 0) + 1/3600
          }));
        }, 1000);
      }
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeTimer, localTimeEntries]);

  // Auto-resize textareas when entries change
  useEffect(() => {
    textareaRefs.current.forEach((textarea) => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    });
  }, [localTimeEntries]);

  // Quick entry keyboard shortcut - type to start entering time
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we should ignore this keypress
      const target = e.target as HTMLElement;
      const isTypingInInput = target.tagName === 'INPUT' || 
                              target.tagName === 'TEXTAREA' || 
                              target.isContentEditable ||
                              target.getAttribute('role') === 'combobox';
      
      // Only trigger if:
      // 1. Not already typing in an input
      // 2. It's a single alphanumeric character
      // 3. No modifier keys (ctrl, alt, meta)
      // 4. The card is visible (component is mounted)
      if (isTypingInInput || 
          e.key.length !== 1 || 
          e.ctrlKey || 
          e.altKey || 
          e.metaKey ||
          !cardRef.current) {
        return;
      }
      
      // Find the last row (new entry row) and trigger its combobox
      if (lastRowTriggerRef.current) {
        // Simulate a click on the combobox trigger to open it
        lastRowTriggerRef.current.click();
        
        // The combobox will handle the keypress naturally when it opens
        // because we're not preventing default here
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const formatMatterName = (matterId: number) => {
    const matter = matters.find(m => m.id === matterId);
    if (!matter) return "Unknown Matter";
    
    if (matter.group_name) {
      return `${matter.group_name} - ${matter.name}`;
    }
    return matter.name;
  };

  const handleEntryChange = (index: number, field: keyof TimeEntryRow, value: any) => {
    // Don't update if the user is currently focused on this field
    if (field === 'hours' && index === focusedHoursIndex) {
      setEditingHoursValue(value);
      return;
    }
    
    if (field === 'description' && index === focusedDescriptionIndex) {
      // User is typing - always accept their input
      const updatedEntries = [...localTimeEntries];
      (updatedEntries[index] as any)[field] = value;
      
      // Clear any existing timeout for this entry
      if ((updatedEntries[index] as any).saveTimeout) {
        clearTimeout((updatedEntries[index] as any).saveTimeout);
      }
      
      // Set up auto-save if this is an existing entry
      if (!updatedEntries[index].isNew && 
          updatedEntries[index].matter && 
          updatedEntries[index].description) {
        (updatedEntries[index] as any).saveTimeout = setTimeout(() => {
          saveTimeEntry(index);
        }, settings.autoSaveDelay);
      }
      
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      return;
    }
    
    if (field === 'matter' && index === focusedMatterIndex) {
      // User is selecting matter - always accept their input
      const updatedEntries = [...localTimeEntries];
      (updatedEntries[index] as any)[field] = value;
      
      // Clear any existing timeout for this entry
      if ((updatedEntries[index] as any).saveTimeout) {
        clearTimeout((updatedEntries[index] as any).saveTimeout);
      }
      
      // Set up auto-save if this is an existing entry
      if (!updatedEntries[index].isNew && 
          updatedEntries[index].matter && 
          updatedEntries[index].description) {
        (updatedEntries[index] as any).saveTimeout = setTimeout(() => {
          saveTimeEntry(index);
        }, settings.autoSaveDelay);
      }
      
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      return;
    }
    
    // For all other cases, update normally
    const updatedEntries = [...localTimeEntries];
    (updatedEntries[index] as any)[field] = value;
    
    // Clear any existing timeout for this entry
    if ((updatedEntries[index] as any).saveTimeout) {
      clearTimeout((updatedEntries[index] as any).saveTimeout);
    }
    
    // Only set up auto-save if this is an existing entry (not new)
    // and both required fields are filled
    if (!updatedEntries[index].isNew && 
        updatedEntries[index].matter && 
        updatedEntries[index].description) {
      (updatedEntries[index] as any).saveTimeout = setTimeout(() => {
        saveTimeEntry(index);
      }, settings.autoSaveDelay);
    }
    
    setLocalTimeEntries(updatedEntries);
    setTimeEntries(updatedEntries);

    // Auto-add new row logic - only check after state is updated
    // and only if we're editing the last row
    if (index === localTimeEntries.length - 1) {
      const updatedMatter = field === 'matter' ? value : localTimeEntries[index].matter;
      const updatedDescription = field === 'description' ? value : localTimeEntries[index].description;
      const updatedHours = field === 'hours' ? value : localTimeEntries[index].hours;
      
      // Add a new row when the user has filled in enough information
      if ((updatedMatter > 0 || updatedDescription) && updatedHours) {
        // Use setTimeout to avoid state update conflicts
        setTimeout(() => {
          if (updatedMatter && updatedDescription) {
            addTimeEntryRow();
          }
        }, 100);
      }
    }
  };

  const formatHoursOnBlur = (index: number, rawValue: string) => {
    const valueToParse = editingHoursValue !== null ? editingHoursValue : rawValue;
    const decimalHours = parseTimeInput(valueToParse, settings);
    
    // Validate hours
    const validation = validateHoursInput(decimalHours, settings);
    if (!validation.isValid) {
      toast({
        title: 'Invalid hours',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    const formattedDecimalString = decimalHours.toFixed(settings.hoursRounding);

    const updatedEntries = [...localTimeEntries];
    updatedEntries[index].hours = formattedDecimalString;
    setLocalTimeEntries(updatedEntries);
    setTimeEntries(updatedEntries);

    if (updatedEntries[index].matter && updatedEntries[index].description) {
      clearTimeout((updatedEntries[index] as any).saveTimeout);
      (updatedEntries[index] as any).saveTimeout = setTimeout(() => {
        saveTimeEntry(index);
      }, settings.autoSaveDelay);
    }
    
    setFocusedHoursIndex(null);
    setEditingHoursValue(null);
  };

  const handleHoursFocus = (index: number) => {
    setFocusedHoursIndex(index);
    if (settings.timeFormat === 'hhmm') {
      setEditingHoursValue(decimalToHHMM(localTimeEntries[index].hours, settings));
    } else {
      setEditingHoursValue(localTimeEntries[index].hours);
    }
  };

  const saveTimeEntry = async (index: number): Promise<number | undefined> => {
    const entry = localTimeEntries[index];
    if (!entry.matter || !entry.description) return;

    // While the timer is running the backend refuses PUT/PATCH on
    // time-related fields (the timer ledger is the source of truth).
    // Skip the autosave instead of round-tripping a 403.
    if (entry.timer_active) {
      return entry.id;
    }

    if (settings.requireMatterSelection && !entry.matter) {
      toast({
        title: 'Matter required',
        description: 'Please select a matter before saving',
        variant: 'destructive',
      });
      return;
    }
    
    if (settings.requireDescription && !entry.description.trim()) {
      toast({
        title: 'Description required',
        description: 'Please provide a description before saving',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const method = entry.isNew ? 'post' : 'put';
      const endpoint = entry.isNew 
        ? ROUTES.API.TIMESHEET 
        : `${ROUTES.API.TIMESHEET}${entry.id}/`;
      
      const payload = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        matter: entry.matter,
        description: entry.description,
        hours: parseFloat(entry.hours || '0'),
        user: user?.id
      };
      
      const response = await api[method](endpoint, payload);

      // Remember the matter for recents-first ordering in matter comboboxes
      pushRecentMatter(String(entry.matter));

      if (entry.isNew) {
        const updatedEntries = [...localTimeEntries];
        updatedEntries[index] = {
          ...entry,
          id: response.data.id,
          isNew: false,
          matterName: formatMatterName(entry.matter),
          originalValues: { 
            matter: payload.matter,
            description: payload.description,
            hours: payload.hours.toString(),
            billable: entry.billable
          }
        };
        
        setLocalTimeEntries(updatedEntries);
        setTimeEntries(updatedEntries);
      }
      
      if (entry.isNew) {
        toast({
          title: 'Success',
          description: 'Time entry created',
        });
      } else {
        toast({
          title: 'Success',
        description: 'Time entry auto-saved',
      });
      }
      
      // Don't refresh time entries on save - we've already updated local state
      // Only refresh stats to keep the dashboard statistics accurate
      onRefreshStats();
      return response.data.id;    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save time entry',
        variant: 'destructive',
      });
      return undefined;
    }
  };

  const deleteTimeEntry = async (index: number) => {
    const entry = localTimeEntries[index];
    if (entry.isNew) {
      const updatedEntries = [...localTimeEntries];
      updatedEntries.splice(index, 1);
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      return;
    }
    
    try {
      await api.delete(`${ROUTES.API.TIMESHEET}${entry.id}/`);
      
      const updatedEntries = [...localTimeEntries];
      updatedEntries.splice(index, 1);
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      
      // Don't refresh time entries on delete - we've already updated local state
      // Only refresh stats to keep the dashboard statistics accurate
      onRefreshStats();
      
      toast({
        title: 'Success',
        description: 'Time entry deleted',
      });
      
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete time entry',
        variant: 'destructive',
      });
    }
  };

  const startTimer = async (index: number) => {
    // Prevent concurrent operations (race condition fix)
    if (operationInProgressRef.current) return;
    operationInProgressRef.current = true;
    
    const entry = localTimeEntries[index];
    let entryId = entry.id;
    
    if (settings.requireMatterSelection && !entry.matter) {
      toast({
        title: 'Missing information',
        description: 'Please select a matter before starting timer',
        variant: 'destructive',
      });
      operationInProgressRef.current = false;
      return;
    }
    
    if (settings.requireDescription && !entry.description.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please provide a description before starting timer',
        variant: 'destructive',
      });
      operationInProgressRef.current = false;
      return;
    }
    
    try {
      // Cancel any pending auto-save timers to prevent duplicate creates
      if ((entry as any).saveTimeout) {
        clearTimeout((entry as any).saveTimeout);
      }
      
      // If it's a new entry, use atomic create_and_start_timer endpoint
      if (entry.isNew || !entryId) {
        if (!entry.matter || !entry.description) {
          toast({
            title: 'Missing information',
            description: 'Please select a matter and provide a description',
            variant: 'destructive',
          });
          operationInProgressRef.current = false;
          return;
        }
        
        const payload = {
          date: format(selectedDate, 'yyyy-MM-dd'),
          matter: entry.matter,
          description: entry.description,
          hours: parseFloat(entry.hours || '0'),
          user: user?.id
        };
        
        const response = await api.post(
          `${ROUTES.API.TIMESHEET}create_and_start_timer/`,
          payload
        );
        
        entryId = response.data.timesheet.id;
        pushRecentMatter(String(entry.matter));

        const updatedEntries = [...localTimeEntries];
        updatedEntries[index] = {
          ...entry,
          id: entryId,
          isNew: false,
          timer_active: true,
          matterName: formatMatterName(entry.matter),
          originalValues: {
            matter: payload.matter,
            description: payload.description,
            hours: payload.hours.toString(),
            billable: entry.billable
          }
        };
        setLocalTimeEntries(updatedEntries);
        setTimeEntries(updatedEntries);
        setActiveTimer(entryId || null);
        
        if (settings.timerNotifications) {
          toast({
            title: 'Timer Started',
            description: 'Entry created and timer started',
          });
        }
        
        onRefreshStats();
        operationInProgressRef.current = false;
        return;
      }
      
      await api.post(`${ROUTES.API.TIMESHEET}${entryId}/start_timer/`);
      setActiveTimer(entryId || null);
      
      const updatedEntries = [...localTimeEntries];
      updatedEntries[index].timer_active = true;
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      
      if (settings.timerNotifications) {
        toast({
          title: 'Timer Started',
          description: 'Recording time',
        });
      }
      
    } catch (error) {
      console.error('Start timer error:', error);
      showErrorToast(error, 'Error', 'Failed to start timer');
    } finally {
      operationInProgressRef.current = false;
    }
  };

  const stopTimer = async (index: number) => {
    if (settings.timerConfirmStop) {
      // Could add a confirmation dialog here
    }
    
    const entry = localTimeEntries[index];
    if (!entry.id) return;
    
    try {
      await api.post(`${ROUTES.API.TIMESHEET}${entry.id}/stop_timer/`);
      
      setActiveTimer(null);
      
      const updatedEntries = [...localTimeEntries];
      updatedEntries.forEach(entry => {
        entry.timer_active = false;
      });
      
      updatedEntries[index].isEditing = true;
      setLocalTimeEntries(updatedEntries);
      setTimeEntries(updatedEntries);
      
      setElapsedTime({});
      
      if (settings.timerNotifications) {
        toast({
          title: 'Timer Stopped',
          description: 'Time recorded successfully',
        });
      }
      
      // Refresh both time entries and stats when stopping timer
      // This ensures we get the accurate hours recorded by the timer
      onRefreshTimeEntries();
      onRefreshStats();
      
    } catch (error) {
      showErrorToast(error, 'Error', 'Failed to stop timer');
    }
  };

  const addTimeEntryRow = () => {
    const newEntries = [
      ...localTimeEntries,
      { 
        matter: 0, 
        description: '', 
        hours: '', 
        billable: settings.defaultBillable, 
        isNew: true, 
        isEditing: true,
        originalValues: {
          matter: 0,
          description: '',
          hours: '',
          billable: settings.defaultBillable
        }
      }
    ];
    setLocalTimeEntries(newEntries);
    setTimeEntries(newEntries);
  };

  const previousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(selectedDate.getDate() - 1);
    onDateChange(prevDay);
  };

  const nextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(selectedDate.getDate() + 1);
    onDateChange(nextDay);
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  return (
    <Card ref={cardRef}>
      <CardHeader className={settings.compactMode ? 'pb-3' : 'pb-2'}>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className={settings.compactMode ? 'text-lg' : 'text-xl'}>
              Daily Time Entries
            </CardTitle>          
          </div>
          
          {activeTimer && (
            <div 
              className="flex items-center px-3 py-1 rounded-full"
              style={{ backgroundColor: `${settings.timerActiveColor}20`, color: settings.timerActiveColor }}
            >
              <Clock className="h-4 w-4 mr-2 animate-pulse" />
              <span className="font-medium">Timer Running</span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className={settings.compactMode ? 'pt-3' : 'pt-4'}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={previousDay}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Previous Day
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={nextDay}>
              Next Day
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <DatePicker
              value={toISODate(selectedDate)}
              onChange={(iso) => {
                if (iso) onDateChange(parseISO(iso));
              }}
              clearable={false}
              placeholder="Jump to date"
              className="h-8 w-auto"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {format(selectedDate, settings.dateFormat)}
          </div>
        </div>

        <div>
          {/* Table Header */}
          <div className={`grid grid-cols-20 gap-2 px-2 py-2 ${settings.showGridLines ? 'bg-muted/20' : 'bg-muted/10'} rounded-md font-medium text-sm text-muted-foreground`}>
            <div className="col-span-8">Group - Matter Name</div>
            <div className="col-span-8">Description</div>
            <div className="col-span-2 text-center">Hours</div>
            <div className="col-span-1 text-center">Timer</div>
            <div className="col-span-1 text-center">Delete</div>
          </div>
          
          {/* Time Entries */}
          {localTimeEntries.map((entry, index) => (
            <div 
              key={index} 
              className={`grid grid-cols-20 gap-2 items-start py-3 px-2 rounded-md ${settings.showGridLines ? 'border-b last:border-0' : ''} 
                ${entry.isNew ? '' : 'hover:bg-muted/10'}
                ${entry.timer_active ? `bg-opacity-10` : ''}`}
              style={entry.timer_active ? { backgroundColor: `${settings.timerActiveColor}20` } : {}}
            >
              <div className="col-span-8">
                <div 
                  className="space-y-1"
                  onFocus={() => setFocusedMatterIndex(index)}
                  onBlur={() => setFocusedMatterIndex(null)}
                >
                  <ComboboxSelect
                    options={matterOptions}
                    value={entry.matter ? entry.matter.toString() : undefined}
                    onValueChange={(value) => handleEntryChange(index, 'matter', Number(value))}
                    placeholder="Select matter"
                    searchPlaceholder="Filter matters..."
                    emptyMessage="No matters found."
                    preserveOrder
                    disabled={entry.timer_active}
                    popoverClassName="w-[500px]"
                    triggerRef={index === localTimeEntries.length - 1 ? lastRowTriggerRef : undefined}
                  />
                </div>
              </div>
              
              <div className="col-span-8">
                <Textarea
                  ref={(el) => {
                    textareaRefs.current[index] = el;
                  }}
                  placeholder="Description of work"
                  value={entry.description}
                  onChange={(e) => {
                    handleEntryChange(index, 'description', e.target.value);
                    // Adjust height on change
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = target.scrollHeight + 'px';
                  }}
                  onFocus={() => setFocusedDescriptionIndex(index)}
                  onBlur={() => setFocusedDescriptionIndex(null)}
                  disabled={entry.timer_active}
                  className="min-h-[36px] resize-none overflow-hidden py-2"
                  rows={1}
                />
              </div>
              
              <div className="col-span-2">
                <Input
                  placeholder={settings.timeFormat === 'hhmm' ? "HH:MM" : "0.00"}
                  value={
                    index === focusedHoursIndex
                      ? (editingHoursValue ?? '')
                      : entry.timer_active && entry.id
                        ? formatTimeForDisplay(elapsedTime[entry.id] || parseFloat(entry.hours) || 0, settings)
                        : formatTimeForDisplay(entry.hours, settings)
                  }
                  onChange={(e) => handleEntryChange(index, 'hours', e.target.value)}
                  onFocus={() => handleHoursFocus(index)}
                  onBlur={(e) => formatHoursOnBlur(index, e.target.value)}
                  disabled={entry.timer_active}
                  className="text-center h-9 w-full"
                />
              </div>
              
              <div className="col-span-1 text-center">
                {entry.timer_active ? (
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => stopTimer(index)}
                    title="Stop Timer"
                  >
                    <Pause className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => startTimer(index)}
                    disabled={activeTimer !== null || !entry.matter || !entry.description || operationInProgressRef.current}
                    title="Start Timer"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div className="col-span-1 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteTimeEntry(index)}
                  disabled={entry.timer_active}
                  className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Delete entry"
                >
                  <Trash className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
            </div>
          ))}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={addTimeEntryRow}
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Time Entry
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
